import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';
import { SearchState } from './search/search.reducer';
import { GeneralState } from './general.reducer';
import { ContactState } from './contact/contact.reducer';
import { AppLanguage, Breadcrumbs, GuidelineDTO } from '../models';
import { PagesState } from './pages/pages.reducer';
import { LocalizationState } from '@ca/ca-ng-core';
import { GuidelinesState } from './guidelines.reducer';

//#region Router
export const selectRouter = createFeatureSelector<RouterReducerState>('router');

export const selectCurrentRoute = createSelector(
  selectRouter,
  (routerState) => routerState?.state?.url.split('/').filter((f) => f != '') ?? [],
);

export const selectRouteParams = createSelector(
  selectRouter,
  (routerState) => routerState?.state?.root?.params ?? {},
);
//#endregion

//#region Page
export const selectPages = createFeatureSelector<PagesState>('pages');
export const selectPagesRoot = createSelector(selectPages, s => s.root);
export const selectCMSPage = (path: string[], language: AppLanguage) =>
  createSelector(selectPages, (p) => p.cms[`${language}:${path.join('/')}`]);
export const selectActivePage = createSelector(selectPages, (s) => s.activePage);
//#endregion

//#region General
export const SelectLocalization = createFeatureSelector<LocalizationState<AppLanguage>>('localization');
export const selectGeneral = createFeatureSelector<GeneralState>('general');
export const selectUserRole = createSelector(selectGeneral, (s) => s.role);
export const selectLanguage = createSelector(SelectLocalization, (s) => s.language);
export const selectMenuFilters = createSelector(selectGeneral, (s) => s.navigationFilters);
export const selectHomeName = createSelector(selectGeneral, s => s.translates.home);
export const selectActivePageAndRole = createSelector(selectPages, selectGeneral, (s, g) => ({
  activePage: s.activePage,
  role:
    g.navigationFilters.find((r) => r.filter === g.role)?.name ?? null,
}))
//#endregion

//#region Breadcrumbs
export const HOME_BREADCRUMB = {
  en: {
    metaUrl: 'home',
    routerLink: [],
    title: 'Home',
  },
  fr: {
    metaUrl: 'acceuil',
    routerLink: [],
    title: 'D’accueil',
  },
  nl: {
    metaUrl: 'home',
    routerLink: [],
    title: 'Home',
  },
};

export const selectGeneralDataForBreadcrumbs = (s: {
  general: GeneralState;
  localization: LocalizationState<AppLanguage>;
}) => ({
  role: s.general.role,
  roles: s.general.navigationFilters,
  language: s.localization.language,
  home: s.general.translates.home
});

export const selectBreadcrumbs = createFeatureSelector<Breadcrumbs>('breadcrumbs');
//#endregion

//#region Header
export const selectHeaderData = (s: {
  general: GeneralState;
  localization: LocalizationState;
}) => ({
  mainMenuGroups: s.general.navigationGroups,
  menuTriggers: s.general.navigationFilters,
  role: s.general.role,
  language: s.localization.language,
});
//#endregion

//#region Footer
export const selectFooterData = createSelector(selectGeneral, (s) => ({
  mainMenuGroups: s.navigationGroups,
  navItems: s.navigationFilters,
  settings: s.settings,
  translates: s.translates,
}));
//#endregion

//#region Search
export const selectSearch = createFeatureSelector<SearchState>('search');
export const selectSearchResults = createSelector(selectSearch, (s) => s.results);
export const selectSearchInput = createSelector(selectSearch, (s) => s.searchString);
export const selectSearchPage = createSelector(selectPages, (s) => s.search);
export const selectNoResultsMessage = createSelector(selectSearch, s => s.message);
//#endregion

//#region Contact
export const selectContact = createFeatureSelector<ContactState>('contact');
export const selectContactPage = createSelector(selectPages, (s) => s.contact);
//#endregion

//#region Privacy and Cookies
export const selectPrivacyAndCookiesPage = createSelector(selectPages, (p) => p.privacyPolicy);
//#endregion

//#region Guidelines
export const selectGuidelinesState = createFeatureSelector<GuidelinesState>('guidelines');
export const selectAllGuidelines = createSelector(
  selectGuidelinesState,
  state => state.strategies.flatMap(strategy => strategy.guidelines)
);
export const selectStrategies = createSelector(selectGuidelinesState, (state) => state.strategies);
export const selectAreAllGuidelinesSelected = createSelector(
  selectGuidelinesState,
  (state) => {
    const allIds = state.strategies.flatMap(s => s.guidelines.map(g => g.id));
    return allIds.length > 0 && allIds.every(id => state.selectedRows.has(id));
  }
);
export const selectGuidelinesSelectedRows = createSelector(
  selectGuidelinesState,
  (state) => state.selectedRows,
);
export const selectGuidelinesUi = createSelector(
  selectGuidelinesState,
  state => state.ui
);
export const selectOpenGuidelinesStrategyId = createSelector(
  selectGuidelinesUi,
  ui => ui.openStrategyId
);
export const selectFocusGuidelineId = createSelector(
  selectGuidelinesUi,
  ui => ui.focusGuidelineId
);
export const selectFilters = createSelector(
  selectGuidelinesState,
  state => state.filters
);
export const selectStrategiesWithFilteredGuidelines = createSelector(
  selectStrategies,
  selectFilters,
  (strategies, filters) => {
    return strategies
      .map(strategy => ({
        ...strategy,
        guidelines: strategy.guidelines.filter(guideline => {
          let matches = true;
          // Apply category filter if specified
          if (filters.categories && filters.categories.length) {
            matches = matches && filters.categories.some((cat: string) => guideline.categories.includes(cat));
          }
          // Apply preset filter if specified
          if (filters.presets && filters.presets.length) {
            matches = matches && filters.presets.some((preset: string) => guideline.presets.includes(preset));
          }
          return matches;
        })
      }))
      .filter(strategy => strategy.guidelines.length > 0);
  }
);
export const selectTranslationsForGuidelines = createSelector(
  selectGeneral,
  selectGuidelinesSelectedRows,
  selectAllGuidelines,
  (s, selectedRows, allGuidelines) => ({
    categoryFilterLabel: s.translates['guidelines-filter-categories'] ?? 'Categories',
    presetFilterLabel: s.translates['guidelines-filter-presets'] ?? 'Presets',
    downloadButtonText: s.translates['guidelines-download-btn-text'] ?? 'Download',
    clearButtonText: s.translates['clear-guidelines-filters'],
    detailButtonText: s.translates['guidelines-detail-btn-text'],

    chart1Title: s.translates['guidelines-chart1-title'],
    chart1Intro: s.translates['guidelines-chart1-intro']
      .replace('[SELECTED_COUNT]', String(selectedRows.size))
      .replace('[TOTAL_COUNT]', String(allGuidelines.length)),
    chart2Title: s.translates['guidelines-chart2-title'],
    chart2Intro: s.translates["guidelines-chart2-intro"],

    zoomBtnText: s.translates['click-to-zoom'],
    columns: {
      title: s.translates['guidelines-table-header-title'],
      description: s.translates['guidelines-table-header-description'],
      presets: s.translates['guidelines-table-header-presets'],
      category: s.translates['guidelines-table-header-category'],
      action: s.translates['guidelines-table-header-action'],
    }
  })
);

//#endregion
